import _ from "lodash";
import * as DateUtils from "../../../utils/date/date";
import { AvailablePeriod } from "../../services/AvailablePeriod/AvailablePeriod";
import { sumPointsInRange } from "../../../utils/year-to-date-comparation";
import { withoutEmptySlices } from "../../../utils/hide-empty-chart-series";

/**
 * @class
 * Classe cuida dos series do chart
 */
export class HighchartsFormatSeriesAdapter {
    constructor({ ColorService, FormatUnits } = {}) {
        this.types = {
            area: this.temporalSeries,
            arearange: this.temporalSeries,
            areaspline: this.temporalSeries,
            bar: this.temporalSeries,
            column: this.temporalSeries,
            columncomparation: this.nameValueComparation,
            columnrange: this.temporalSeries,
            line: this.temporalSeries,
            spline: this.temporalSeries,
            pie: this.nameValue,
            variablepie: this.nameValue,
            scatter: -1,
            funnel: 4,
            pyramid: 4,
            gauge: this.nameValue,
            solidgauge: 2,
            waterfall: this.nameValue,
            donut: this.donut,
            combinations: this.temporalSeries
        };

        this.FormatUnits = FormatUnits;
        this.ColorService = ColorService;
        this.AvailablePeriod = new AvailablePeriod();
    }

    /**
     * @private
     * Formata de acrodo com a necessidade dos gráficos
     * @param {Object} date
     * @param {Boolean} parse
     * @returns {Object} date
     */
    _formatDate = (date, parse = false) => {
        date = DateUtils.dateToString(DateUtils.getStartOfMonth(date), "T");
        return parse ? +date : date;
    };

    /**
     * @private
     *
     * @param {Object} dates
     * @return {Array}
     */
    _getDates = (dates = this.dates) => {
        if (!dates) return;

        const response = dates.map((date) => this._formatDate(date));

        return _.uniq(response).sort();
    };

    /**
     * @private
     * @param {String} _id
     * @returns {Object}
     */
    _getSerie = (_id) => {
        return _.find(this.series, (serie) => serie._id == _id);
    };

    /**
     * @private
     * @param {String} _id
     * @returns {Object}
     */
    _getColor = (color) => {
        return this.ColorService.colorByPath(color);
    };

    /**
     * @private
     * Faz união de duas series
     * @param {Array} seriesWithPoints series gerada pela
     * @param {Array} seriesChart series gerada pelo chart dentro do BD
     * @returns {Array}
     */
    _mergeSeries = (seriesWithPoints, seriesChart = this.seriesDefault) => {
        seriesWithPoints = [].concat(seriesWithPoints);
        seriesWithPoints = seriesWithPoints.map((serie) => {
            let defaultSerie = seriesChart.find((serieChart) => {
                return [serie._id].includes(serieChart._id);
            });
            if (!defaultSerie) {
                defaultSerie = seriesChart.find((serieChart) => {
                    return [serie.referenceId].includes(serieChart._id);
                });
            }
            defaultSerie = angular.copy(defaultSerie);
            if (defaultSerie) {
                const color = this._getColor(defaultSerie.color);

                if (color) defaultSerie.color = color;

                if (this.type == "donut") {
                    if (_.get(this.text, "value") >= 0) {
                        serie.data[0].color =
                            defaultSerie.color || this._getColor("yellow.dark");
                        serie.data[1].color = "#e8ebeb";
                    } else {
                        serie.data[0].color = "#e8ebeb";
                        serie.data[1].color =
                            defaultSerie.color || this._getColor("yellow.dark");
                    }
                }
            }

            _.unset(defaultSerie, "data");

            if (defaultSerie)
                defaultSerie.stack = serie.stack || defaultSerie.stack;

            return Object.assign(serie, defaultSerie);
        });
        return seriesWithPoints;
    };

    _getColorPattern({
        point,
        color,
        type,
        currentComparation = this.currentComparation,
        budget = this.budget
    }) {
        if (point.p || budget) return false;
        if (type === "line") return false;

        const pattern = {
            pattern: {
                path: {
                    d: "M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11",
                    strokeWidth: 3
                },
                width: 10,
                height: 10,
                color: this._getColor(color) || color,
                opacity: 1
            }
        };

        if (!color) return false;

        if (point.p) return pattern;

        if (currentComparation.allMonths || budget) return pattern;

        if (
            currentComparation.active &&
            +point.t === +currentComparation.date &&
            point.sc === currentComparation.scenario._id
        ) {
            return pattern;
        }

        return false;
    }

    getXAxisIndex(point) {
        return point.t.getUTCMonth();
    }

    /**
     * @public
     * Cria series temporais
     * @returns {Object}
     * @returns {Array}
     */
    temporalSeries = () => {
        if (!this.series.length) {
            return [];
        }

        const seriesToChart = [];

        if (this.currentComparation.allMonths) {
            const years = [
                {
                    value: this.currentComparation.date.getUTCFullYear(),
                    stack: "comparation",
                    scenario: this.currentComparation.scenario
                },
                {
                    value: this.currentDate.getUTCFullYear(),
                    stack: "period",
                    scenario: this.scenario
                }
            ];

            years.forEach((year, yearIndex) => {
                this.series.forEach((serie, index) => {
                    const points = this.points.filter(
                        (p) =>
                            serie._id == p.s &&
                            p.t.getUTCFullYear() === year.value &&
                            p.sc === year.scenario._id
                    );

                    const serieDefault = this.getSerieDefault(serie);

                    const pointsToChart = points.map((point) => {
                        const pointYear = point.t.getUTCFullYear();

                        const isComparationPoint =
                            this.currentComparation.date.getUTCFullYear() ===
                                pointYear && year.stack === "comparation";

                        return {
                            metadata: {
                                year: year.value,
                                scenario: _.get(year, "scenario.name"),
                                description: serie.description
                            },
                            x: this.getXAxisIndex(point),
                            y: this.getValue(serie, point, serieDefault),
                            color: isComparationPoint
                                ? this._getColorPattern({
                                      point,
                                      color: _.get(serieDefault, "color"),
                                      type: _.get(serieDefault, "type")
                                  })
                                : false
                        };
                    });

                    let serieProps = {};

                    if (this.series.length >= 5) {
                        serieProps.type = "line";
                        serieDefault.type = "line";
                    }

                    if (year.stack === "comparation") {
                        if (_.get(serieDefault, "type") === "line") {
                            serieProps.dashStyle = "dash";
                        }
                    }

                    if (year.stack === "comparation") {
                        serieProps.linkedTo = serie._id;
                    }

                    seriesToChart.push({
                        _id: serie._id,
                        id: serie._id,
                        unit: serie.unit,
                        name: serie.name,
                        data: pointsToChart,
                        ...serieProps,
                        stack: year.stack,
                        metadata: {
                            year: year.value,
                            scenario: year.scenario.name
                        }
                    });
                });
            });
        } else if (this.budget) {
            const budgets = [
                {
                    stack: "period",
                    scenario: { _id: "ORIGINAL", name: "Original" }
                },
                {
                    stack: "comparation",
                    scenario: { _id: this.budget, name: "Orçamento" }
                }
            ];

            budgets.forEach((budget) => {
                this.series
                    .filter((s) => s.origin !== "goals")
                    .forEach((serie, index) => {
                        const points = this.points.filter(
                            (p) =>
                                serie._id == p.s && p.sc === budget.scenario._id
                        );
                        const serieDefault = this.getSerieDefault(serie);
                        const pointsToChart = points.map((point) => {
                            const pointYear = point.t.getUTCFullYear();

                            const isComparationPoint =
                                budget.stack === "comparation";

                            return {
                                metadata: {
                                    year: pointYear,
                                    scenario: _.get(budget, "scenario.name")
                                },
                                y: this.getValue(serie, point, serieDefault),
                                x: this._getXAxiCategory(point),
                                name: this.AvailablePeriod.getPeriodLabels(
                                    point.t,
                                    this.period
                                ),
                                opacity: isComparationPoint ? 0.5 : 1,
                                color: isComparationPoint
                                    ? this._getColorPattern({
                                          point,
                                          color: _.get(serieDefault, "color"),
                                          type: _.get(serieDefault, "type")
                                      })
                                    : false
                            };
                        });

                        let serieProps = {};

                        if (budget.stack === "comparation") {
                            if (_.get(serieDefault, "type") === "line") {
                                serieProps.opacity = 0.5;
                            }
                        }

                        if (budget.stack === "comparation") {
                            serieProps.linkedTo = serie._id;
                        }
                        const serieToChart = {
                            _id: serie._id,
                            id: serie._id,
                            unit: serie.unit,
                            name: serie.name,
                            data: pointsToChart,
                            ...serieProps,
                            stack: budget.stack,
                            metadata: {
                                description: serie.description,
                                scenario: budget.scenario.name
                            }
                        };

                        seriesToChart.push(serieToChart);
                    });
            });
            if (this.goals) {
                this.series
                    .filter((s) => s.origin == "goals")
                    .forEach((serie) => {
                        const serieDefault = this.getSerieDefault(serie);
                        const points = this.points.filter(
                            (p) => serie._id == p.s
                        );
                        const pointsToChart = points.map((point) => {
                            const pointYear = point.t.getUTCFullYear();
                            return {
                                metadata: {
                                    year: pointYear,
                                    scenario: budgets[0]?.scenario?.name
                                },
                                name: this.AvailablePeriod.getPeriodLabels(
                                    point.t,
                                    this.period
                                ),
                                y: this.getValue(serie, point, serieDefault),
                                x: this._getXAxiCategory(point)
                            };
                        });
                        const serieToChart = {
                            ...serie,
                            data: pointsToChart,
                            metadata: {
                                description: serie.description,
                                scenario: budgets[0]?.scenario?.name
                            }
                        };
                        if (serieToChart.origin === "goals") {
                            serieToChart.marker = false;
                            serieToChart.lineWidth = 3;
                        }
                        seriesToChart.push(serieToChart);
                    });
            }
        } else {
            const isSameDate =
                +this.currentComparation.date === +this.currentDate;

            const scenarios = [
                {
                    name: "Cenário Base",
                    ...this.scenario
                },
                {
                    name: "Cenário Comparado",
                    ...this.currentComparation.scenario
                }
            ];

            this.series.forEach((serie, index) => {
                const points = this.points.filter((p) => serie._id == p.s);
                const serieDefault = this.getSerieDefault(serie);
                const pointsToChart = points.map((point, index) => {
                    let resultPoint = null;

                    const scenario = scenarios.find((s) => s._id === point.sc);

                    if (
                        (["yearly", "quarterly", "semester"].includes(
                            this.period
                        ) &&
                            !this.rewriteCategories) ||
                        isSameDate
                    ) {
                        resultPoint = {
                            y: this.getValue(serie, point, serieDefault),
                            metadata: {
                                description: serie.description,
                                scenario: _.get(scenario, "name", ""),
                                projection: point.p
                            },
                            opacity: point.p ? 0.5 : 1,
                            color: this._getColorPattern({
                                point,
                                color: _.get(serieDefault, "color"),
                                type: _.get(serieDefault, "type")
                            })
                        };
                    } else {
                        resultPoint = {
                            y: this.getValue(serie, point, serieDefault),
                            x: this._formatDate(_.get(point, "t"), true),
                            metadata: {
                                description: serie.description,
                                scenario: _.get(scenario, "name", ""),
                                projection: point.p
                            },
                            opacity: point.p ? 0.5 : 1,
                            color: this._getColorPattern({
                                point,
                                color: _.get(serieDefault, "color"),
                                type: _.get(serieDefault, "type")
                            })
                        };
                    }

                    return resultPoint;
                });
                const serieToChart = {
                    ...serie,
                    metadata: { description: serie.description },
                    id: serie._id,
                    _id: serie._id,
                    unit: serie.unit,
                    name: serie.name,
                    data: pointsToChart,
                    linkedTo: serie.linkedTo
                };
                if (serieToChart.origin === "goals") {
                    serieToChart.marker = false;
                }
                if (_.get(serieDefault, "type") === "line") {
                    const lastPointOriginal = pointsToChart.findLast(
                        (p) => !p.metadata.projection
                    );
                    const lastPointProjection = pointsToChart.findLast(
                        (p) => p.metadata.projection
                    );
                    const newColor = this.ColorService.getLowerTonality(
                        serieDefault.color
                    );
                    const color = this.ColorService.colorByPath(newColor);
                    if (lastPointOriginal) {
                        serieToChart.zoneAxis = "x";
                        serieToChart.zones = [
                            {
                                value: lastPointOriginal.x + 2678400000
                            },
                            {
                                value: lastPointProjection
                                    ? lastPointProjection.x + 2678400000
                                    : false,
                                color
                            }
                        ];
                    }
                }
                seriesToChart.push(serieToChart);
            });
        }
        const seriesMergeVisualization = this._mergeSeries(seriesToChart);
        return seriesMergeVisualization;
    };

    _getXAxiCategory(point, period = this.period) {
        if (["yearly", "quarterly", "semester"].includes(period))
            return undefined;
        else return +point.t;
    }

    _singleDataComparation = () => {
        const serieToChart = [];

        this.series.forEach((serie) => {
            const point = this.points.find((point) => {
                return point.s == serie._id && +point.t == +this.currentDate;
            });

            const pointComparation = this.points.find((point) => {
                return (
                    point.s == serie._id &&
                    +point.t == +this.currentComparation.date
                );
            });

            serieToChart.push({
                totalPoints: this.totalPoints,
                _id: serie._id,
                name: serie.name,
                unit: serie.unit,
                y: this.getValue(serie, point) || 0
            });

            serieToChart.push({
                totalPoints: this.totalPoints,
                _id: serie._id,
                name: serie.name,
                unit: serie.unit,
                y: this.getValue(serie, pointComparation) || 0
            });
        });

        const total = serieToChart.reduce((a, b) => a + Math.abs(b.y), 0);

        return serieToChart.map((p) => ({
            ...p,
            totalPoints: total
        }));
    };

    /**
     * @private
     * Retorna um array com uma posição contendo as seires
     * @returns {Array}
     */
    _singleData = () => {
        const serieToChart = [];
        // Pizza no modo acumulado: a serie que soma le o periodo das barras. Ver `chartAccumulation`.
        const acumulado = this.accumulation;

        this.series.forEach((serie) => {
            const point = this.points.find((point) => {
                return point.s == serie._id && +point.t == +this.currentDate;
            });
            const somaJanela = acumulado?.seriesIds?.includes(serie._id);

            serieToChart.push({
                totalPoints: this.totalPoints,
                _id: serie._id,
                name: serie.name,
                unit: serie.unit,
                y: somaJanela
                    ? sumPointsInRange(
                          this.points,
                          { ...acumulado, serieId: serie._id },
                          (p) => this.getValue(serie, p)
                      )
                    : this.getValue(serie, point) || 0
            });
        });

        const total = serieToChart.reduce((a, b) => a + Math.abs(b.y), 0);

        return serieToChart.map((p) => ({
            ...p,
            totalPoints: total
        }));
    };

    /**
     * @private
     * Retorna um array com uma possição
     * @returns {Array}
     */
    nameValue = () => {
        let data = [];

        if (this.currentComparation.active) {
            data = this._singleDataComparation();
        } else data = this._singleData();

        data = this._mergeSeries(data);

        data = _.sortBy(data, "i");

        for (let v in data) {
            if (data[v].positive) {
                data[v].raw = data[v].y;
                data[v].y = Math.abs(data[v].y);
            }
        }

        if (this.type == "pie") {
            data.forEach((serie) => {
                if (serie.negativeColor && serie.raw < 0) {
                    serie.color = this._getColor(serie.negativeColor);
                }
            });
        }

        if (this.type == "pie") data = withoutEmptySlices(data);

        return [
            {
                key: true,
                name: null,
                data
            }
        ];
    };

    /**
     * @public
     * Função formata de acordo com o Fonte de Recursos
     * @returns {Array}
     */
    nameValueComparation = () => {
        const serieToChart = [];

        this.series.forEach((serie) => {
            const point = this.points.find((point) => {
                return point.s == serie._id && +point.t == +this.currentDate;
            });

            const seriesDefault =
                _.find(this.seriesDefault, { _id: serie._id }) || {};

            serieToChart.push({
                _id: serie._id,
                data: [
                    {
                        y: this.getValue(serie, point, seriesDefault) || 0,
                        color: seriesDefault.color,
                        name: serie.name
                    }
                ],
                stack: seriesDefault.stack,
                type: seriesDefault.type
            });
        });

        return serieToChart;
    };

    /**
     * @private
     * @param {Number} valor a ser destacada
     * @param {Stirng} unit unidade a ser representada
     *
     */
    _setText(unit, num, fixedValue = 0) {
        this.text = {
            value: num,
            text: this.FormatUnits.formatValue(unit, num, fixedValue)
        };
    }

    /**
     * @private
     * Retorna o valor que deve ser destacado
     * @returns {Object} { text: 100 }
     */
    _getText() {
        return this.text || "";
    }

    /**
     * @private
     * Retona array
     *
     * @return
     */
    donut = () => {
        let [serie] = this._singleData();

        if (!serie) return;

        this._setText(serie.unit, serie.y, 2);

        serie.y = serie.y / 100;

        let data = [
            { y: Math.abs(serie.y) },
            {
                y: 1 - Math.abs(serie.y)
            }
        ];

        serie.data = data;

        serie.data = serie.y < 0 ? data.reverse() : data;

        const [donutSeriesDefault] = this.seriesDefault;

        if (donutSeriesDefault.data) {
            serie.data = _.merge(data, donutSeriesDefault.data);
        }

        serie.data = serie.y < 0 ? serie.data.reverse() : serie.data;

        serie = this._mergeSeries(serie);

        return serie;
    };

    getSerieDefault(serie) {
        return (
            this.seriesDefault.find((s) => {
                return s._id === serie._id || s._id == serie.referenceId;
            }) || {}
        );
    }

    getValue(serie, point, serieChart) {
        let value = 0;
        if (point?.m?.hasOwnProperty("v")) value = point?.m?.v || 0;
        else value = point?.m?.f || 0;
        if (serie.origin == "goals") {
            if (serieChart?.changeSign === "reverse") {
                value = -Math.abs(point?.m?.c) || 0;
            } else {
                value = point?.m?.c || 0;
            }
        }
        return value;
    }

    /**
     * @public
     *
     * @param {String}
     */
    format = ({
        series,
        points,
        dates,
        seriesDefault = [],
        type,
        filters,
        rewriteCategories = false,
        currentComparation = {},
        budget = false,
        goals = false,
        accumulation = null
    }) => {
        this.points = points;
        this.series = series;
        this.currentComparation = currentComparation;
        this.budget = budget;
        this.goals = goals;
        this.accumulation = accumulation;
        this.currentDate = filters.date;
        this.dates = this._getDates(dates);
        this.type = type;
        this.period = filters.period;
        this.scenario = filters.scenario;
        this.seriesDefault = seriesDefault;
        this.rewriteCategories = rewriteCategories;
        if (this.types[type]) {
            return this.types[type]();
        }

        return this.temporalSeries();
    };
}
